import React from 'react'
import styles from './attributeDetails.module.css'
import { LuMove, LuTrash } from 'react-icons/lu'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable'
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers'
import { CSS } from '@dnd-kit/utilities'


function normalizeAttributeValue(rawValue) {
    if (rawValue == null) {
        return null;
    }

    if (typeof rawValue === "string") {
        const trimmed = rawValue.trim();
        return trimmed ? { id: trimmed, value: trimmed, price: 0, duration: 0 } : null;
    }

    const value = String(rawValue?.value ?? rawValue?.Value ?? "").trim();
    if (!value) {
        return null;
    }

    const price = Number(rawValue?.price ?? rawValue?.Price ?? 0);
    const duration = Number(rawValue?.duration ?? rawValue?.Duration ?? 0);

    return {
        id: rawValue?.id != null ? String(rawValue.id) : `${value}-${Number.isFinite(price) ? price : 0}-${Number.isFinite(duration) ? duration : 0}`,
        value,
        price: Number.isFinite(price) ? price : 0,
        duration: Number.isFinite(duration) ? duration : 0,
    };
}

function groupAttributesByName(attributes) {
    const groupedAttributes = new Map();

    (Array.isArray(attributes) ? attributes : []).forEach((attribute, index) => {
        const name = String(attribute?.name ?? attribute?.Name ?? "").trim();
        const attributeId = attribute?.attribute_id ?? attribute?.AttributeID ?? null;
        const required = Boolean(attribute?.required ?? attribute?.Required ?? false);
        const nestedValues = Array.isArray(attribute?.values) ? attribute.values : (Array.isArray(attribute?.Values) ? attribute.Values : []);

        if (Array.isArray(attribute?.values) || Array.isArray(attribute?.Values)) {
            const groupKey = name || `attribute-${index}`;
            if (!groupedAttributes.has(groupKey)) {
                groupedAttributes.set(groupKey, {
                    id: attributeId != null ? String(attributeId) : groupKey,
                    name: name || `Attribute ${index + 1}`,
                    required,
                    valuesByKey: new Map(),
                });
            }

            const group = groupedAttributes.get(groupKey);
            group.required = group.required || required;

            nestedValues
                .map(normalizeAttributeValue)
                .filter(Boolean)
                .forEach((value) => {
                    const uniqueKey = `${value.value}|${value.price}|${value.duration}`;
                    if (!group.valuesByKey.has(uniqueKey)) {
                        group.valuesByKey.set(uniqueKey, value);
                    }
                });

            return;
        }

        const value = normalizeAttributeValue(attribute);
        if (!name || !value) {
            return;
        }

        if (!groupedAttributes.has(name)) {
            groupedAttributes.set(name, {
                id: attributeId != null ? String(attributeId) : name,
                name,
                required,
                valuesByKey: new Map(),
            });
        }

        const group = groupedAttributes.get(name);
        group.required = group.required || required;
        const uniqueKey = `${value.value}|${value.price}|${value.duration}`;
        if (!group.valuesByKey.has(uniqueKey)) {
            group.valuesByKey.set(uniqueKey, value);
        }
    });

    return Array.from(groupedAttributes.values()).map((group) => ({
        id: String(group.id),
        name: group.name,
        required: group.required,
        values: Array.from(group.valuesByKey.values()),
    }));
}

function SortableAttributeCard({ group, index, onRowClick, onAttributeDelete, deleting, disabled }) {
    const {
        attributes: dragAttributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: group.id, disabled });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };



    return (
        <div
            ref={setNodeRef}
            style={style}
            className={styles.attributeCard}
            role="button"
            tabIndex={0}
            onClick={() => !disabled && onRowClick?.(group)}
            onKeyDown={(event) => {
                if (event.target !== event.currentTarget || disabled) return;
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onRowClick?.(group);
                }
            }}
        >
            <div className={styles.attributeCardContent}>
                <div className={styles.attributeCardHeader}>
                    <p className={styles.attributeCardName}>{`${index + 1}. ${group.name}`}</p>
                    {group.required && <span className={styles.attributeRequiredPill}>Required</span>}
                </div>
                <div className={styles.attributeCardValues}>
                    {group.values.map((value) => {
                        const valueText = typeof value === 'string' ? value : (value?.value ?? "");
                        const priceCents = typeof value === 'object' ? Number(value?.price ?? 0) : 0;
                        const duration = typeof value === 'object' ? Number(value?.duration ?? 0) : 0;
                        const meta = [
                            priceCents > 0 ? `$${(priceCents / 100).toFixed(2)}` : null,
                            duration > 0 ? `${Number(duration).toFixed(0)} min` : null,
                        ].filter(Boolean);

                        return (
                            <span key={typeof value === 'string' ? value : `${group.name}-${valueText}-${priceCents}-${duration}`} className={styles.attributeCardValue}>
                                <span>{valueText}</span>
                                {meta.length > 0 && <small>{meta.join(' · ')}</small>}
                            </span>
                        );
                    })}
                </div>
            </div>
            <div className={styles.actionButtonsContainer}>
                <div
                    className={styles.attributeGrabIconContainer}
                    onClick={(event) => event.stopPropagation()}
                    {...dragAttributes}
                    {...listeners}
                >
                    <LuMove size={16} className={styles.attributeActionIcon} />
                </div>
                <button
                    type="button"
                    aria-label={deleting ? `Deleting ${group.name}` : `Delete attribute group ${group.name}`}
                    disabled={disabled || !onAttributeDelete}
                    onClick={(event) => { event.stopPropagation(); onAttributeDelete?.(group); }}
                    className={styles.attributeActionIconContainer}
                >
                    <LuTrash size={16} className={styles.attributeActionIcon} color="var(--danger)"/>
                </button>
            </div>

            

        </div>
    );
}

function AttributeDetails({
    attributes,
    onAddAttribute=() => {},
    onAddIncompatibility=() => {},
    onReorderAttributes=() => {},
    onEditAttribute=() => {},
    onDeleteAttribute,
}) {

    const attributeGroups = React.useMemo(() => groupAttributesByName(attributes), [attributes]);
    const [orderedIds, setOrderedIds] = React.useState([]);
    const [reorderError, setReorderError] = React.useState("");
    const [deletingGroupId, setDeletingGroupId] = React.useState(null);
    const deletingRef = React.useRef(false);
    const [deleteError, setDeleteError] = React.useState("");

    React.useEffect(() => {
        const currentIds = attributeGroups.map((group) => group.id);
        setOrderedIds((previousIds) => {
            const stillPresent = previousIds.filter((id) => currentIds.includes(id));
            const newIds = currentIds.filter((id) => !previousIds.includes(id));
            const nextIds = [...stillPresent, ...newIds];

            const isSame = nextIds.length === previousIds.length
                && nextIds.every((id, index) => id === previousIds[index]);

            return isSame ? previousIds : nextIds;
        });
    }, [attributeGroups]);

    const orderedGroups = React.useMemo(() => {
        const groupsById = new Map(attributeGroups.map((group) => [group.id, group]));
        return orderedIds.map((id) => groupsById.get(id)).filter(Boolean);
    }, [attributeGroups, orderedIds]);

    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: { distance: 4 },
    }));

    const handleDragEnd = async (event) => {
        if (deletingRef.current) return;
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = orderedIds.indexOf(active.id);
        const newIndex = orderedIds.indexOf(over.id);
        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        const previousIds = orderedIds;
        const nextIds = arrayMove(orderedIds, oldIndex, newIndex);
        setOrderedIds(nextIds);
        setReorderError("");

        try {
            await onReorderAttributes(nextIds);
        } catch (error) {
            setOrderedIds(previousIds);
            setReorderError(error?.message || "Failed to save attribute order.");
        }
    };

    const handleDelete = async (group) => {
        if (deletingRef.current || !window.confirm(`Remove "${group.name}" from this service? The shared attribute and its values will not be deleted.`)) {
            return;
        }
        deletingRef.current = true;
        setDeletingGroupId(group.id);
        setDeleteError("");
        try {
            await onDeleteAttribute(group.id);
        } catch (error) {
            setDeleteError(error?.message || "Failed to delete attribute group.");
        } finally {
            deletingRef.current = false;
            setDeletingGroupId(null);
        }
    };

    return (
        <div className={styles.attributesContainer}>
            <div className={styles.attributesHeaderContainer}>
                <div>
                    <h2 className={styles.attributesHeader}>Attributes</h2>
                    <p className={styles.sectionDescription}>Options that adjust this service's price or duration.</p>
                </div>
                <div className={styles.headerActions}>
                <button type="button" className={styles.rulesButton} onClick={onAddIncompatibility}>Manage incompatibilities</button>
                <button type="button" className={styles.addAttributeButton} onClick={onAddAttribute}>
                    Add Attributes
                </button>
                </div>
            </div>

            {orderedGroups.length > 0 ? (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
                        {orderedGroups.map((group, index) => (
                            <SortableAttributeCard
                                key={group.id}
                                group={group}
                                index={index}
                                onRowClick={(clickedGroup) => onEditAttribute(clickedGroup.id)}
                                onAttributeDelete={onDeleteAttribute ? handleDelete : undefined}
                                deleting={deletingGroupId === group.id}
                                disabled={deletingGroupId !== null}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            ) : (
                <div className={styles.emptyAttributes}>
                    <p>No attributes yet.</p>
                </div>
            )}
            {reorderError && <p role="alert">{reorderError}</p>}
            {deleteError && <p role="alert">{deleteError}</p>}
            {deletingGroupId !== null && <p role="status">Deleting attribute group...</p>}

        </div>
    )
}

export default AttributeDetails
